/**
 * Two memos used by Room3DView to project the equipment_list onto the 3D scene:
 *
 *   useStagedItems(project, opts) — materialises positions for every load
 *     (unplaced items are parked outside the room as a "staging area"),
 *     then optionally filters down to the user's isolate selection.
 *
 *   useComplianceById(report, loadList) — maps each load id to "error" |
 *     "warning" | undefined based on validation report issues, so the
 *     scene can tint boxes accordingly.
 */
import { useMemo } from "react";

export function useStagedItems(project, { isolate, selected, selectedSet }) {
  const noRoom = !project.length_in || !project.width_in;
  const loadList = project.equipment_list || [];

  return useMemo(() => {
    if (noRoom) return [];
    let stagingOffset = 6;
    let items = loadList.map((it) => {
      if (it.position && it.position.x_in !== undefined) return it;
      const w = it.length_in || 24;
      const d = it.width_in  || 18;
      const fakePos = { x_in: -w - 12, y_in: stagingOffset, w_in: w, h_in: d };
      stagingOffset += d + 8;
      return { ...it, position: fakePos, _unplaced: true };
    });
    if (isolate) {
      const keep = selectedSet.size > 0
        ? selectedSet
        : (selected ? new Set([selected]) : new Set());
      items = items.filter((it) => keep.has(it.id));
    }
    return items;
  }, [loadList, noRoom, isolate, selectedSet, selected]);
}

export function useComplianceById(report, loadList) {
  return useMemo(() => {
    const map = {};
    if (!report) return map;
    for (const i of report.issues || []) {
      if (!i.tag) continue;
      const id = loadList.find((it) => (it.tag || it.equipment_type) === i.tag)?.id;
      if (!id) continue;
      const cur = map[id];
      if (cur === "error") continue;
      if (i.level === "error") map[id] = "error";
      else if (i.level === "warning" && cur !== "error") map[id] = "warning";
    }
    return map;
  }, [report, loadList]);
}
